/**
 * m3 echo Frontend - Main App Component
 *
 * Definiert Routing und Layout-Struktur der Anwendung.
 */

import React from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import ProjectDetail from './pages/ProjectDetail';
import Settings from './pages/Settings';
import TeaserAnalysis from './pages/TeaserAnalysis';
import { Login } from './pages/Login';
import { Impressum } from './pages/Impressum';
import { Lizenzen } from './pages/Lizenzen';
import { SSOCallback } from './pages/SSOCallback';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminRoute } from './components/AdminRoute';
import { PasswordChangePage } from './pages/PasswordChangePage';
import OnboardingPage from './pages/OnboardingPage';
import { useIsAuthenticated } from './stores/useAuthStore';
import { useIdentityIsolation } from './hooks/useIdentityIsolation';
import { queryClient } from './utils/queryClient';

// Admin pages (TenantManagement, UserManagement, CostReporting are now embedded in AdminDashboard)
import AdminDashboard from './pages/admin/AdminDashboard';
import Tools from './pages/Tools';
import { Billing } from './pages/Billing';
import WikiPage from './pages/wiki/WikiPage';

const App: React.FC = () => {
  const isAuthenticated = useIsAuthenticated();

  /**
   * Cache and WebSocket connection belong to whoever is currently signed in.
   *
   * Both used to be handled here, and both were a step behind: the cache was
   * dropped from an effect, i.e. after the render that had already published
   * the new identity, and the connection reacted only to `isAuthenticated`,
   * which does not change when one signed-in user is replaced by another.
   * See `hooks/useIdentityIsolation`.
   */
  useIdentityIsolation();

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/lizenzen" element={<Lizenzen />} />

          {/* SSO callback route - public (user not yet authenticated from frontend's perspective) */}
          <Route path="/sso-callback" element={<SSOCallback />} />

          {/* Password change route - requires authentication but skips mustChangePassword check */}
          <Route path="/change-password" element={<PasswordChangePage />} />

          {/* Onboarding route - requires authentication but bypasses the
              "needs onboarding" redirect in ProtectedRoute (to avoid a loop).
              Unauthenticated users are bounced to /login. */}
          <Route
            path="/onboarding"
            element={
              isAuthenticated ? <OnboardingPage /> : <Navigate to="/login" replace />
            }
          />

          {/* Protected routes - single persistent Layout via Outlet */}
          <Route
            element={
              <ProtectedRoute>
                <Layout>
                  <Outlet />
                </Layout>
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="projects/:id" element={<ProjectDetail />} />
            <Route path="settings" element={<Settings />} />
            <Route path="teaser" element={<TeaserAnalysis />} />

            {/* Admin routes - require admin privileges */}
            <Route path="admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route path="tools" element={<AdminRoute><Tools /></AdminRoute>} />
            <Route path="billing" element={<AdminRoute><Billing /></AdminRoute>} />
            <Route path="wiki" element={<AdminRoute><WikiPage /></AdminRoute>} />

            {/* Legacy admin routes - redirect to unified /admin */}
            <Route path="admin/tenants" element={<Navigate to="/admin" replace />} />
            <Route path="admin/users" element={<Navigate to="/admin" replace />} />
            <Route path="admin/costs" element={<Navigate to="/admin" replace />} />
          </Route>

          {/* Fallback - redirect to dashboard or login */}
          <Route
            path="*"
            element={
              isAuthenticated ? (
                <Navigate to="/" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
